import { useEffect, useMemo, useRef, useState } from 'react';
import { IconCheck } from '@tabler/icons-react';
import toast from 'react-hot-toast';
import { UiInput, Dropdown } from 'ui-kit';
import type { DropdownOption } from 'ui-kit/Dropdown/Dropdown';
import { ButtonWithIcon } from 'ui-kit/Buttons';
import type {
  RemittanceMetricItem,
  RemittanceMetricTone,
  RemittanceTotalsCents,
} from './mockRemittanceData';
import { formatCents, formatPaymentMonth } from './utils';
import { useSetRemittanceWireDetails } from './setRemittanceWireDetails_gql';
import type { WireDetailsShape } from './getDealWireDetails_gql';
import type { GetDealMonthRemittances_getDealMonthRemittances_item } from './__generated__/GetDealMonthRemittances';
import type { SetRemittanceWireDetailsInput } from '__generated__/globalTypes';

const toneClass = (tone?: RemittanceMetricTone) => {
  if (tone === 'positive') return 'text-green-400';
  if (tone === 'negative') return 'text-red-400';
  return 'text-white';
};

type DealRole = 'BUYER' | 'SELLER';

const EMPTY_WIRE_DETAILS: WireDetailsShape = {
  account_name: '',
  account_number: '',
  bank_name: '',
  routing_number: '',
};

type RemittanceMetricsProps = {
  /** When provided, used instead of mock metrics. */
  metrics?: RemittanceMetricItem[] | null;
  totalsCents?: RemittanceTotalsCents | null;
  /** Deal bid transfer amount 0–100 (investor share). Default 100. */
  transferPercent?: number;
  /** Current user's role on the deal. Drives buyer vs seller wire-details UX. */
  dealRole?: DealRole;
  /** GraphQL deal ID for mutations. */
  dealId?: string;
  /** Remittance snapshot for the selected payment month. */
  dealMonthItem: GetDealMonthRemittances_getDealMonthRemittances_item | null;
  dealMonthLoading?: boolean;
  /** Month picker options in YYYY-MM, descending (newest first). */
  availablePaymentMonths: string[];
  selectedPaymentMonth?: string;
  onSelectPaymentMonth: (paymentMonth: string) => void;
  /** Refetches the snapshot; caller invokes this on version-mismatch errors. */
  refetchDealMonth: () => void;
};

function WireDetailsReadOnly({
  title,
  details,
  emptyMessage = 'Not provided yet',
}: {
  title: string;
  details: WireDetailsShape | null;
  emptyMessage?: string;
}) {
  return (
    <div className="space-y-3 border-t border-gray-800 pt-4">
      <div className="text-[12px] font-semibold uppercase text-gray-300">{title}</div>
      {details ? (
        <div className="grid grid-cols-[100px_1fr] gap-x-4 gap-y-2 text-[12px]">
          <span className="uppercase text-gray-400">Account name</span>
          <span className="text-white">{details.account_name}</span>
          <span className="uppercase text-gray-400">Account number</span>
          <span className="text-white">{details.account_number}</span>
          <span className="uppercase text-gray-400">Bank name</span>
          <span className="text-white">{details.bank_name}</span>
          <span className="uppercase text-gray-400">Routing number</span>
          <span className="text-white">{details.routing_number}</span>
        </div>
      ) : (
        <p className="text-[12px] text-gray-500">{emptyMessage}</p>
      )}
    </div>
  );
}

function WireDetailsForm({
  title,
  value,
  onChange,
  disabled,
  idPrefix,
}: {
  title: string;
  value: WireDetailsShape;
  onChange: (v: WireDetailsShape) => void;
  disabled?: boolean;
  /** Unique prefix so the two forms that can render side-by-side don't collide on `id`. */
  idPrefix: 'buyer' | 'seller';
}) {
  const id = (field: keyof WireDetailsShape) => `${idPrefix}-${field}`;
  return (
    <div className="space-y-3 border-t border-gray-800 pt-4">
      <div className="text-[12px] font-semibold uppercase text-gray-300">{title}</div>
      <div className="grid grid-cols-[100px_1fr] items-center gap-x-4 gap-y-2">
        <label htmlFor={id('account_name')} className="text-[12px] uppercase text-gray-400">
          Account name
        </label>
        <UiInput
          id={id('account_name')}
          variant="gray"
          size="small"
          value={value.account_name}
          disabled={disabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange({ ...value, account_name: e.target.value })
          }
        />
        <label htmlFor={id('account_number')} className="text-[12px] uppercase text-gray-400">
          Account number
        </label>
        <UiInput
          id={id('account_number')}
          variant="gray"
          size="small"
          value={value.account_number}
          disabled={disabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange({ ...value, account_number: e.target.value })
          }
        />
        <label htmlFor={id('bank_name')} className="text-[12px] uppercase text-gray-400">
          Bank name
        </label>
        <UiInput
          id={id('bank_name')}
          variant="gray"
          size="small"
          value={value.bank_name}
          disabled={disabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange({ ...value, bank_name: e.target.value })
          }
        />
        <label htmlFor={id('routing_number')} className="text-[12px] uppercase text-gray-400">
          Routing number
        </label>
        <UiInput
          id={id('routing_number')}
          variant="gray"
          size="small"
          value={value.routing_number}
          disabled={disabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange({ ...value, routing_number: e.target.value })
          }
        />
      </div>
    </div>
  );
}

function buildTwoColumnMetrics(
  totals: RemittanceTotalsCents,
  transferPercent: number,
): { investor: RemittanceMetricItem[]; servicer: RemittanceMetricItem[] } {
  const pct = Math.max(0, Math.min(100, transferPercent)) / 100;
  const investorPct = pct;
  const servicerPct = 1 - pct;
  const principal = totals.principal_cents ?? 0;
  const interest = totals.interest_cents ?? 0;
  const servicing = totals.servicing_cents ?? 0;
  const investorTotalCents = principal * investorPct + interest * investorPct;
  const servicerTotalCents = principal * servicerPct + interest * servicerPct + servicing;
  const investor = [
    { label: 'Total cash', value: formatCents(investorTotalCents) },
    { label: 'Principal', value: formatCents(principal * investorPct) },
    { label: 'Interest', value: formatCents(interest * investorPct) },
    { label: 'Servicing fee', value: formatCents(0), valueTone: 'negative' as const },
  ];
  const servicer = [
    { label: 'Total cash', value: formatCents(servicerTotalCents) },
    { label: 'Principal', value: formatCents(principal * servicerPct) },
    { label: 'Interest', value: formatCents(interest * servicerPct) },
    { label: 'Servicing fee', value: formatCents(servicing), valueTone: 'positive' as const },
  ];
  return { investor, servicer };
}

/**
 * Strip Apollo cache metadata (`__typename`) and normalise to a plain `WireDetailsShape`.
 * The Apollo client (`InMemoryCache`, default `addTypename: true`) attaches `__typename`
 * to every cached object, and passing that straight into a mutation input is rejected by
 * the server as an unknown field. See `convertCarveFiltersToCleanFilters` in
 * `BidCardBody/index.tsx` for the same pattern elsewhere in the codebase.
 */
function cleanWireDetails(wd: WireDetailsShape | null | undefined): WireDetailsShape {
  if (!wd) return EMPTY_WIRE_DETAILS;
  return {
    account_name: wd.account_name,
    account_number: wd.account_number,
    bank_name: wd.bank_name,
    routing_number: wd.routing_number,
  };
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function isVersionMismatchError(err: unknown): boolean {
  if (!err) return false;
  // Tight match on the known loan-service phrasings; anything else falls through to the
  // generic "Failed to save" toast rather than triggering a false-positive refetch.
  return /version\s+mismatch|expected\s+version|optimistic\s+lock/i.test(errorMessage(err));
}

const metricWeightClass = (label: string): string =>
  label.toLowerCase().startsWith('total') ? 'font-semibold' : 'font-normal';

const MetricRow = ({ item }: { item: RemittanceMetricItem }) => {
  const weightClass = metricWeightClass(item.label);
  return (
    <div className="mb-1">
      <div className={`text-[12px] uppercase text-gray-400 ${weightClass}`}>{item.label}</div>
      <div className={`text-[12px] ${weightClass} ${toneClass(item.valueTone)}`}>{item.value}</div>
    </div>
  );
};

const MetricColumn = ({ title, items }: { title: string; items: RemittanceMetricItem[] }) => (
  <div>
    <div className="pb-1 text-[12px] font-semibold uppercase text-gray-300">{title}</div>
    {items.map((item) => (
      <MetricRow key={item.label} item={item} />
    ))}
  </div>
);

export const RemittanceMetrics = ({
  metrics,
  totalsCents,
  transferPercent = 100,
  dealRole,
  dealId,
  dealMonthItem,
  dealMonthLoading = false,
  availablePaymentMonths,
  selectedPaymentMonth,
  onSelectPaymentMonth,
  refetchDealMonth,
}: RemittanceMetricsProps) => {
  // `transferPercent` has a default of 100 so it's never null here; the only branch we
  // need is on `totalsCents`. Keep the memoized computation for stable identity.
  const twoColumns = useMemo(
    () => (totalsCents != null ? buildTwoColumnMetrics(totalsCents, transferPercent) : null),
    [totalsCents, transferPercent],
  );

  const buyerWireDetails = dealMonthItem?.buyer_wire_details ?? null;
  const sellerWireDetails = dealMonthItem?.seller_wire_details ?? null;
  const buyerConfirmed = dealMonthItem?.buyer_remittances_received ?? false;
  const sellerConfirmed = dealMonthItem?.seller_remittances_sent ?? false;

  // Backend rejects wire-detail updates once the buyer confirms receipt; we treat that
  // as the snapshot-locked state for both sides.
  const snapshotLocked = buyerConfirmed;

  const [buyerFormValue, setBuyerFormValue] = useState<WireDetailsShape>(EMPTY_WIRE_DETAILS);
  const [sellerFormValue, setSellerFormValue] = useState<WireDetailsShape>(EMPTY_WIRE_DETAILS);

  // Dirty flags guard against poll-driven refetches (cache-and-network) clobbering
  // in-progress edits. Flip true on first user edit; cleared after successful mutation
  // and whenever the payment-month selection changes.
  const buyerFormDirtyRef = useRef(false);
  const sellerFormDirtyRef = useRef(false);

  // Month switch: always reset both forms and clear their dirty flags.
  useEffect(() => {
    buyerFormDirtyRef.current = false;
    sellerFormDirtyRef.current = false;
    setBuyerFormValue(cleanWireDetails(buyerWireDetails));
    setSellerFormValue(cleanWireDetails(sellerWireDetails));
  }, [selectedPaymentMonth]);

  // Server-driven updates: only sync if the user hasn't started typing.
  useEffect(() => {
    if (buyerFormDirtyRef.current) return;
    setBuyerFormValue(cleanWireDetails(buyerWireDetails));
  }, [buyerWireDetails]);

  useEffect(() => {
    if (sellerFormDirtyRef.current) return;
    setSellerFormValue(cleanWireDetails(sellerWireDetails));
  }, [sellerWireDetails]);

  const onBuyerFormChange = (v: WireDetailsShape) => {
    buyerFormDirtyRef.current = true;
    setBuyerFormValue(v);
  };
  const onSellerFormChange = (v: WireDetailsShape) => {
    sellerFormDirtyRef.current = true;
    setSellerFormValue(v);
  };

  const [setWireDetails, { loading: saving }] = useSetRemittanceWireDetails();

  const monthOptions: DropdownOption[] = useMemo(
    () =>
      availablePaymentMonths.map((pm) => ({
        value: pm,
        label: formatPaymentMonth(pm) ?? pm,
      })),
    [availablePaymentMonths],
  );

  const runMutation = async (input: {
    buyer_wire_details?: WireDetailsShape;
    seller_wire_details?: WireDetailsShape;
    buyer_remittances_received?: boolean;
    seller_remittances_sent?: boolean;
    successMessage: string;
  }) => {
    if (!dealId || !selectedPaymentMonth) {
      toast.error('Missing deal context; cannot save.');
      return;
    }
    if (!dealMonthItem) {
      // Prevents sending `existing_version: 0` for a not-yet-loaded snapshot, which the
      // server rejects as a version mismatch and we'd then mis-classify as concurrent edit.
      toast.error('Snapshot not loaded yet; try again in a moment.');
      return;
    }

    // Role-gated field writes. The UI already branches on `isBuyer` / `isSeller` and
    // only surfaces "your side"'s form, so this is defense-in-depth against refactors
    // or callers that hand-assemble the mutation input.
    if (!dealRole) {
      toast.error('Unknown deal role; cannot save.');
      return;
    }
    if (dealRole !== 'BUYER' && input.buyer_wire_details != null) {
      toast.error('Only the buyer can edit buyer wire details.');
      return;
    }
    if (dealRole !== 'SELLER' && input.seller_wire_details != null) {
      toast.error('Only the seller can edit seller wire details.');
      return;
    }
    if (dealRole !== 'BUYER' && input.buyer_remittances_received != null) {
      toast.error('Only the buyer can confirm receipt.');
      return;
    }
    if (dealRole !== 'SELLER' && input.seller_remittances_sent != null) {
      toast.error('Only the seller can confirm remittance.');
      return;
    }

    // Build the mutation input with only the fields actually being written. Omit
    // unchanged fields entirely — don't send explicit `null` for "don't touch", because
    // that conflates "untouched" and "clear" and leans on the API resolver's null→
    // undefined coercion staying in place.
    const mutationInput: SetRemittanceWireDetailsInput = {
      deal_id: dealId,
      payment_month: selectedPaymentMonth,
      existing_version: dealMonthItem.version,
    };
    if (input.buyer_wire_details !== undefined) {
      mutationInput.buyer_wire_details = input.buyer_wire_details;
    }
    if (input.seller_wire_details !== undefined) {
      mutationInput.seller_wire_details = input.seller_wire_details;
    }
    if (input.buyer_remittances_received !== undefined) {
      mutationInput.buyer_remittances_received = input.buyer_remittances_received;
    }
    if (input.seller_remittances_sent !== undefined) {
      mutationInput.seller_remittances_sent = input.seller_remittances_sent;
    }

    try {
      await setWireDetails({ variables: { input: mutationInput } });
      // Server took the write; the refetch (awaitRefetchQueries: true) will land
      // the new server state. Clear the dirty flag for whichever side we just wrote
      // so the effect can sync the refetched values in.
      if (input.buyer_wire_details != null) buyerFormDirtyRef.current = false;
      if (input.seller_wire_details != null) sellerFormDirtyRef.current = false;
      toast.success(input.successMessage);
    } catch (err) {
      if (isVersionMismatchError(err)) {
        // Clear dirty flags so the server-sync effects actually reseed the forms
        // with the refetched values — otherwise the user keeps seeing their stale
        // edits and can't review what the other party changed.
        buyerFormDirtyRef.current = false;
        sellerFormDirtyRef.current = false;
        toast.error('Another update happened; refreshed — please try again.');
        refetchDealMonth();
      } else {
        toast.error(`Failed to save: ${errorMessage(err) || 'unknown error'}`);
      }
    }
  };

  const handleBuyerSaveWire = () =>
    runMutation({ buyer_wire_details: buyerFormValue, successMessage: 'Wire details saved.' });
  const handleBuyerConfirm = () =>
    runMutation({
      buyer_wire_details: buyerFormValue,
      buyer_remittances_received: true,
      successMessage: 'Remittance receipt confirmed.',
    });
  const handleSellerSaveWire = () =>
    runMutation({ seller_wire_details: sellerFormValue, successMessage: 'Wire details saved.' });
  const handleSellerConfirm = () =>
    runMutation({
      seller_wire_details: sellerFormValue,
      seller_remittances_sent: true,
      successMessage: 'Remittance send confirmed.',
    });

  const isBuyer = dealRole === 'BUYER';
  const isSeller = dealRole === 'SELLER';

  const selectedMonthOption = monthOptions.find((o) => o.value === selectedPaymentMonth);

  return (
    <div className="space-y-6">
      <div className="space-y-2" role="group" aria-label="Payment month">
        <div
          className="text-[12px] font-semibold uppercase text-gray-300"
          id="payment-month-heading"
        >
          Payment month
        </div>
        {monthOptions.length > 0 ? (
          <Dropdown
            options={monthOptions}
            value={selectedPaymentMonth}
            placeholder={selectedMonthOption?.label ?? 'Select month'}
            variant="gray"
            onValueChange={(opt) => onSelectPaymentMonth(opt.value)}
            aria-labelledby="payment-month-heading"
          />
        ) : (
          <p className="text-[12px] text-gray-500">No remittance months available yet.</p>
        )}
      </div>

      <div className="space-y-1">
        {twoColumns ? (
          <div className="grid grid-cols-2 gap-x-4">
            <MetricColumn title="Investor" items={twoColumns.investor} />
            <MetricColumn title="Servicer" items={twoColumns.servicer} />
          </div>
        ) : metrics && metrics.length > 0 ? (
          metrics.map((item) => <MetricRow key={item.label} item={item} />)
        ) : (
          <p className="text-[12px] text-gray-500">No metrics available.</p>
        )}
      </div>

      <div className="pt-2 text-[12px] font-semibold uppercase text-gray-300">Wire information</div>

      {snapshotLocked && (
        <div className="rounded border border-gray-800 bg-gray-900/40 px-3 py-2 text-[12px] text-gray-300">
          Buyer confirmed receipt for this payment month; the snapshot is locked and wire details
          can no longer be edited.
        </div>
      )}

      {snapshotLocked ? (
        <>
          <WireDetailsReadOnly title="Buyer wire details" details={buyerWireDetails} />
          <WireDetailsReadOnly title="Seller wire details" details={sellerWireDetails} />
        </>
      ) : isBuyer ? (
        <>
          <WireDetailsForm
            title="Your wire details"
            idPrefix="buyer"
            value={buyerFormValue}
            onChange={onBuyerFormChange}
            disabled={saving || dealMonthLoading}
          />
          <WireDetailsReadOnly title="Seller wire details" details={sellerWireDetails} />
          <div className="mt-4 flex flex-col gap-2">
            <ButtonWithIcon
              label={saving ? 'Saving…' : 'Save wire details'}
              icon={<IconCheck size={16} />}
              size="medium"
              type="secondary"
              iconPosition="right"
              disabled={saving || dealMonthLoading}
              onClick={handleBuyerSaveWire}
              className="w-full justify-center"
            />
            <ButtonWithIcon
              label={
                buyerConfirmed
                  ? 'Receipt confirmed'
                  : saving
                    ? 'Confirming…'
                    : 'Confirm receiving payment'
              }
              icon={<IconCheck size={16} />}
              size="medium"
              type="primary"
              iconPosition="right"
              disabled={saving || dealMonthLoading || buyerConfirmed}
              onClick={handleBuyerConfirm}
              className="w-full justify-center"
            />
          </div>
        </>
      ) : isSeller ? (
        <>
          <WireDetailsReadOnly title="Buyer wire details" details={buyerWireDetails} />
          <WireDetailsForm
            title="Your wire details"
            idPrefix="seller"
            value={sellerFormValue}
            onChange={onSellerFormChange}
            disabled={saving || dealMonthLoading}
          />
          <div className="mt-4 flex flex-col gap-2">
            <ButtonWithIcon
              label={saving ? 'Saving…' : 'Save wire details'}
              icon={<IconCheck size={16} />}
              size="medium"
              type="secondary"
              iconPosition="right"
              disabled={saving || dealMonthLoading}
              onClick={handleSellerSaveWire}
              className="w-full justify-center"
            />
            <ButtonWithIcon
              label={
                sellerConfirmed
                  ? 'Remittance confirmed'
                  : saving
                    ? 'Confirming…'
                    : 'Confirm remittance of funds'
              }
              icon={<IconCheck size={16} />}
              size="medium"
              type="primary"
              iconPosition="right"
              disabled={saving || dealMonthLoading || sellerConfirmed}
              onClick={handleSellerConfirm}
              className="w-full justify-center"
            />
          </div>
        </>
      ) : (
        <p className="border-t border-gray-800 pt-4 text-[12px] text-gray-500">
          Open a deal to view wire details as buyer or seller.
        </p>
      )}
    </div>
  );
};
